import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import type { InterventionRoomPropertyResponse } from '../../api/generated';
import { InterventionRoomPropertyControllerApi } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import styles from './DeleteInterventionRoomPropertyModal.module.css';

interface DeleteInterventionRoomPropertyModalProps {
  property: InterventionRoomPropertyResponse;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteInterventionRoomPropertyModal = ({
  property,
  onClose,
  onDeleted
}: DeleteInterventionRoomPropertyModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [inUseError, setInUseError] = useState(false);

  const handleConfirm = async () => {
    try {
      const config = createApiConfiguration();
      const api = new InterventionRoomPropertyControllerApi(config);
      await api.deleteInterventionRoomProperty({ propertyId: property.id });
      onDeleted();
    } catch (error) {
      console.error('Failed to delete intervention room property:', error);
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        typeof error.response.data === 'string'
      ) {
        setInUseError(true);
        return;
      }
      dispatch(showError({ message: t('delete-intervention-room-property-error') }));
    }
  };

  return (
    <Modal
      title={t('delete-intervention-room-property-title')}
      onClose={onClose}
      width={480}
      footer={
        <div className={styles.footer}>
          {inUseError ? (
            <Button type="primary" onClick={onClose}>
              {t('delete-intervention-room-property-close')}
            </Button>
          ) : (
            <>
              <Button type="secondary" onClick={onClose}>
                {t('delete-room-cancel')}
              </Button>
              <Button type="primary" onClick={handleConfirm}>
                {t('delete-intervention-room-property-confirm')}
              </Button>
            </>
          )}
        </div>
      }
    >
      <p className={styles.message}>
        {inUseError
          ? t('delete-intervention-room-property-in-use')
          : t('delete-intervention-room-property-message', { name: property.name })}
      </p>
    </Modal>
  );
};
